import { submissionActionTypes } from '@actions/actionTypes/submission';
import { submissionInitialState, SubmissionState } from './initialState/submission';
import _cloneDeep from 'lodash/cloneDeep';
import { AnyAction } from 'redux';
import { OriginBlockFormValueKey } from '@enums';
import {
  OriginEntitySectionsOriginSectionEntityInterface,
  OriginSectionEntityInterface,
} from '../api/interfaces';
import { OriginSectionEntityBlocksOriginBlockEntityInterface } from '@types';
import { newOriginBlock } from '@constants/newOriginBlock';
import { generateUUIDV4 } from '@utils';
import isEqual from 'lodash/isEqual';

export const submissionReducer = (
  state = submissionInitialState,
  action: AnyAction
): SubmissionState => {
  switch (action.type) {
    case submissionActionTypes.SET_CURRENT_SUBMISSION: {
      return {
        ...state,
        currentSubmission: _cloneDeep(action.payload),
        currentSubmissionMap: action.payload
          ? {
              ...state.currentSubmissionMap,
              [action.payload.id]: _cloneDeep(action.payload),
            }
          : {},
      };
    }

    case submissionActionTypes.SET_CURRENT_SUBMISSION_INITIAL_DATA: {
      return { ...state, currentSubmissionInitialData: action.payload };
    }

    case submissionActionTypes.SET_CURRENT_SUBMISSION_INITIAL_TITLE: {
      return { ...state, currentSubmissionInitialTitle: action.payload };
    }

    case submissionActionTypes.SET_CURRENT_SUBMISSION_INITIAL_DESCRIPTION: {
      return { ...state, currentSubmissionInitialDescription: action.payload };
    }

    case submissionActionTypes.SET_SUBMISSIONS: {
      return { ...state, submissions: action.payload };
    }
    case submissionActionTypes.SET_CHECKED_SUBMISSIONS: {
      return {
        ...state,
        checkedSubmissions: [...Array.from(new Set(action.payload as string[]))],
      };
    }

    case submissionActionTypes.CURRENT_SUBMISSION_LOADING: {
      return {
        ...state,
        isCurrentSubmissionLoading: action.payload,
      };
    }
    case submissionActionTypes.CURRENT_SUBMISSION_FAILURE: {
      return {
        ...state,
        isCurrentSubmissionFailure: action.payload,
      };
    }
    case submissionActionTypes.CREATE_SUBMISSION_LOADING: {
      return {
        ...state,
        isSubmissionCreateLoading: action.payload,
      };
    }
    case submissionActionTypes.CREATE_SUBMISSION_FAILURE: {
      return {
        ...state,
        isSubmissionCreateFailure: action.payload,
      };
    }
    case submissionActionTypes.DELETE_SUBMISSIONS_LOADING: {
      return {
        ...state,
        isSubmissionsDeleteLoading: action.payload,
      };
    }
    case submissionActionTypes.DELETE_SUBMISSION_FAILURE: {
      return {
        ...state,
        isSubmissionDeleteFailure: action.payload,
      };
    }
    case submissionActionTypes.UPDATE_SUBMISSION_LOADING: {
      return {
        ...state,
        isSubmissionUpdateLoading: action.payload,
      };
    }
    case submissionActionTypes.UPDATE_SUBMISSION_FAILURE: {
      return {
        ...state,
        isSubmissionUpdateFailure: action.payload,
      };
    }
    case submissionActionTypes.DOWNLOAD_SUBMISSION_LOADING: {
      return {
        ...state,
        isSubmissionDownloadLoading: action.payload,
      };
    }
    case submissionActionTypes.DOWNLOAD_SUBMISSION_FAILURE: {
      return {
        ...state,
        isSubmissionDeleteFailure: action.payload,
      };
    }
    case submissionActionTypes.REASSIGN_SUBMISSIONS_GROUP_LOADING: {
      return {
        ...state,
        isReassignSubmissionsGroupLoading: action.payload,
      };
    }
    case submissionActionTypes.REASSIGN_SUBMISSIONS_GROUP_FAILURE: {
      return {
        ...state,
        isReassignSubmissionsGroupFailure: action.payload,
      };
    }
    case submissionActionTypes.CHANGE_SUBMISSIONS_TYPE_LOADING: {
      return {
        ...state,
        isChangeSubmissionsTypeLoading: action.payload,
      };
    }
    case submissionActionTypes.CHANGE_SUBMISSIONS_TYPE_FAILURE: {
      return {
        ...state,
        isChangeSubmissionsTypeFailure: action.payload,
      };
    }
    case submissionActionTypes.NAVIGATE_TO_SUBMISSION: {
      return {
        ...state,
        navigateToSubmission: action.payload,
      };
    }

    case submissionActionTypes.SET_SUBMISSIONS_TOTAL: {
      return { ...state, total: action.payload };
    }

    case submissionActionTypes.SET_TEMPLATE_EDITOR_MODE: {
      return {
        ...state,
        isCurrentSubmissionTemplateEditorModeOn: action.payload,
      };
    }
    case submissionActionTypes.UPDATE_CURRENT_SUBMISSION_ORIGIN_SECTION_NAME: {
      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      const index = currentSubmissionCopy.origin.originSections.findIndex(
        (sectionWithOrder) => sectionWithOrder.id === action.payload.originSectionWithOrderId
      );

      if (index !== -1) {
        currentSubmissionCopy.origin.originSections[index].originSection.name = action.payload.name;
      }

      return {
        ...state,
        currentSubmission: currentSubmissionCopy,
        isCurrentSubmissionTemplateEditorFormChanged: true,
      };
    }
    case submissionActionTypes.CREATE_ORIGIN_SECTION: {
      const { sectionName } = action.payload;
      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      const origin = currentSubmissionCopy.origin;

      const updatedSections = origin.originSections.map((section) => {
        return {
          ...section,
          order: section.order + 1,
        };
      });

      const newSection = {
        id: generateUUIDV4(),
        order: 1,
        originSection: {
          id: generateUUIDV4(),
          name: sectionName,
          originBlocks: [
            {
              id: generateUUIDV4(),
              order: 1,
              originBlock: { ...newOriginBlock, id: generateUUIDV4() },
            },
          ],
        } as OriginSectionEntityInterface,
      } as OriginEntitySectionsOriginSectionEntityInterface;

      const updatedOriginSections = [newSection, ...updatedSections];

      return {
        ...state,
        currentSubmission: {
          ...currentSubmissionCopy,
          origin: {
            ...origin,
            originSections: updatedOriginSections,
          },
        },
        isCurrentSubmissionTemplateEditorFormChanged: true,
      };
    }
    case submissionActionTypes.DELETE_ORIGIN_SECTION: {
      const { originSectionId } = action.payload;
      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      const origin = currentSubmissionCopy.origin;

      const deletedSectionWithOrder = origin.originSections.find(
        (sectionWithOrder) => sectionWithOrder.originSection.id === originSectionId
      );
      if (originSectionId) {
        const newSectionsWithOrder = origin.originSections
          .filter((sectionWithOrder) => sectionWithOrder.originSection.id !== originSectionId)
          .map((sectionWithOrder) => {
            if (deletedSectionWithOrder.order < sectionWithOrder.order) {
              sectionWithOrder.order -= 1;
            }
            return sectionWithOrder;
          });
        origin.originSections = newSectionsWithOrder;
      }

      return {
        ...state,
        currentSubmission: currentSubmissionCopy,
        isCurrentSubmissionTemplateEditorFormChanged: !isEqual(
          state.currentSubmissionDefaultOriginSections,
          currentSubmissionCopy.origin.originSections
        ),
      };
    }
    case submissionActionTypes.UPDATE_ORIGIN_SECTION_BLOCK: {
      const {
        originSectionWithOrderId,
        originSectionBlockWithOrderId,
        updatedBlockFields,
        valueKey,
      } = action.payload;

      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      const originSectionsWithOrder = currentSubmissionCopy.origin.originSections;

      const sectionWithUpdatedBlockIndex = originSectionsWithOrder.findIndex(
        (sectionWithOrder) => sectionWithOrder.id === originSectionWithOrderId
      );

      if (sectionWithUpdatedBlockIndex >= 0) {
        const originSectionBlocksWithOrder =
          originSectionsWithOrder[sectionWithUpdatedBlockIndex].originSection.originBlocks;

        const updatedBlockIndex = originSectionBlocksWithOrder.findIndex(
          (blockWithOrder) => blockWithOrder.id === originSectionBlockWithOrderId
        );

        if (updatedBlockIndex >= 0) {
          if (valueKey === OriginBlockFormValueKey.ContactsType) {
            if (originSectionBlocksWithOrder[updatedBlockIndex].originBlock.id) {
              const defaultSectionWithOrderIndex =
                state.currentSubmissionDefaultOriginSections.findIndex(
                  (sectionWithOrder) => sectionWithOrder.id === originSectionWithOrderId
                );
              // In case when we work with existing section
              if (defaultSectionWithOrderIndex !== -1) {
                const defaultOriginSectionBlocksWithOrder =
                  state.currentSubmissionDefaultOriginSections[defaultSectionWithOrderIndex]
                    .originSection.originBlocks;

                const defaultBlockWithOrderIndex = defaultOriginSectionBlocksWithOrder.findIndex(
                  (blockWithOrder) => blockWithOrder.id === originSectionBlockWithOrderId
                );

                if (defaultBlockWithOrderIndex !== -1) {
                  originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsType =
                    updatedBlockFields.contactsType;

                  // If default origin block doesn't have contactsName need to change it contactsName
                  if (
                    !defaultOriginSectionBlocksWithOrder[defaultBlockWithOrderIndex].originBlock
                      .contactsName
                  ) {
                    if (updatedBlockFields.contactsType) {
                      originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName =
                        originSectionBlocksWithOrder[updatedBlockIndex].originBlock.id;
                    } else {
                      originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName = '';
                    }
                  }

                  // If default block have contactsName
                  if (
                    defaultOriginSectionBlocksWithOrder[defaultBlockWithOrderIndex].originBlock
                      .contactsName
                  ) {
                    // In case when we set type as null and we don't need key now
                    if (!updatedBlockFields.contactsType) {
                      originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName = '';
                    } else {
                      // In case when we firstly set type to null and next return back as value, which means that key should be same with old
                      originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName =
                        defaultOriginSectionBlocksWithOrder[
                          defaultBlockWithOrderIndex
                        ].originBlock.contactsName;
                    }
                  }
                }
                // In case when we work with new block
                if (defaultBlockWithOrderIndex === -1) {
                  originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsType =
                    updatedBlockFields.contactsType;

                  // If new origin block doesn't have contactsName need to change it contactsName
                  if (!originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName) {
                    if (updatedBlockFields.contactsType) {
                      originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName =
                        originSectionBlocksWithOrder[updatedBlockIndex].originBlock.id;
                    } else {
                      originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName = '';
                    }
                  }

                  // If new block have contactsName
                  if (originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName) {
                    // In case when we set type as null and we don't need key now
                    if (!updatedBlockFields.contactsType) {
                      originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName = '';
                    }
                  }
                }
              }
              // In case when we work with new section
              if (defaultSectionWithOrderIndex === -1) {
                originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsType =
                  updatedBlockFields.contactsType;

                // If new origin block doesn't have contactsName need to change it contactsName
                if (!originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName) {
                  if (updatedBlockFields.contactsType) {
                    originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName =
                      originSectionBlocksWithOrder[updatedBlockIndex].originBlock.id;
                  } else {
                    originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName = '';
                  }
                }

                // If new block have contactsName
                if (originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName) {
                  // In case when we set type as null and we don't need key now
                  if (!updatedBlockFields.contactsType) {
                    originSectionBlocksWithOrder[updatedBlockIndex].originBlock.contactsName = '';
                  }
                }
              }
            }
          }

          if (valueKey !== OriginBlockFormValueKey.ContactsType) {
            originSectionBlocksWithOrder[updatedBlockIndex].originBlock = {
              ...originSectionBlocksWithOrder[updatedBlockIndex].originBlock,
              ...updatedBlockFields,
            };
          }
        }
      }

      return {
        ...state,
        currentSubmission: currentSubmissionCopy,
        isCurrentSubmissionTemplateEditorFormChanged: !isEqual(
          state.currentSubmissionDefaultOriginSections,
          currentSubmissionCopy.origin.originSections
        ),
      };
    }
    case submissionActionTypes.DELETE_ORIGIN_SECTION_BLOCK: {
      const { originBlockId, originSectionWithOrderId } = action.payload;

      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      const originSectionsWithOrder = currentSubmissionCopy.origin.originSections;

      const sectionWithDeletedBlockIndex = originSectionsWithOrder.findIndex(
        (sectionWithOrder) => sectionWithOrder.id === originSectionWithOrderId
      );

      const originSectionBlocksWithOrder =
        originSectionsWithOrder[sectionWithDeletedBlockIndex].originSection.originBlocks;

      const deletedBlockWithOrder = originSectionBlocksWithOrder.find(
        (blockWithOrder) => blockWithOrder.originBlock.id === originBlockId
      );

      if (sectionWithDeletedBlockIndex !== -1) {
        const newBlocksWithOrder = originSectionBlocksWithOrder
          .filter((blockWithOrder) => blockWithOrder.originBlock.id !== originBlockId)
          .map((blockWithOrder) => {
            if (deletedBlockWithOrder.order < blockWithOrder.order) {
              blockWithOrder.order -= 1;
            }
            return blockWithOrder;
          });

        originSectionsWithOrder[sectionWithDeletedBlockIndex].originSection.originBlocks =
          newBlocksWithOrder;
      }

      return {
        ...state,
        currentSubmission: currentSubmissionCopy,
        isCurrentSubmissionTemplateEditorFormChanged: !isEqual(
          state.currentSubmissionDefaultOriginSections,
          currentSubmissionCopy.origin.originSections
        ),
      };
    }

    case submissionActionTypes.CREATE_ORIGIN_SECTION_BLOCK: {
      const { originSectionWithOrderId } = action.payload;
      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      const originSectionsWithOrder = currentSubmissionCopy.origin.originSections;

      const sectionWithCreatedBlockIndex = originSectionsWithOrder.findIndex(
        (sectionWithOrder) => sectionWithOrder.id === originSectionWithOrderId
      );

      const originSectionBlocksWithOrder =
        originSectionsWithOrder[sectionWithCreatedBlockIndex].originSection.originBlocks;

      if (originSectionBlocksWithOrder) {
        originSectionsWithOrder[sectionWithCreatedBlockIndex].originSection.originBlocks = [
          ...originSectionsWithOrder[sectionWithCreatedBlockIndex].originSection.originBlocks,
          {
            id: generateUUIDV4(),
            order: originSectionBlocksWithOrder.length + 1,
            originBlock: { ...newOriginBlock, id: generateUUIDV4() },
          } as OriginSectionEntityBlocksOriginBlockEntityInterface,
        ];
      }

      return {
        ...state,
        currentSubmission: currentSubmissionCopy,
        isCurrentSubmissionTemplateEditorFormChanged: !isEqual(
          state.currentSubmissionDefaultOriginSections,
          currentSubmissionCopy.origin.originSections
        ),
      };
    }

    case submissionActionTypes.UPDATE_ORIGIN_SECTION_BLOCKS_ORDER: {
      const { startIndex, endIndex, originSectionWithOrderId } = action.payload;

      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      const originSectionsWithOrder = currentSubmissionCopy.origin.originSections;

      const sectionWithUpdatedBlocksIndex = originSectionsWithOrder.findIndex(
        (sectionWithOrder) => sectionWithOrder.id === originSectionWithOrderId
      );

      if (sectionWithUpdatedBlocksIndex >= 0) {
        const originSectionBlocksWithOrder =
          originSectionsWithOrder[sectionWithUpdatedBlocksIndex].originSection.originBlocks;

        // Updating blocks order logic
        const [removed] = originSectionBlocksWithOrder.splice(startIndex, 1);
        originSectionBlocksWithOrder.splice(endIndex, 0, removed);
        originSectionsWithOrder[sectionWithUpdatedBlocksIndex].originSection.originBlocks =
          originSectionBlocksWithOrder.map((blockWithOrder, index) => ({
            ...blockWithOrder,
            order: index + 1,
          }));
      }

      return {
        ...state,
        currentSubmission: currentSubmissionCopy,
        isCurrentSubmissionTemplateEditorFormChanged: !isEqual(
          state.currentSubmissionDefaultOriginSections,
          currentSubmissionCopy.origin.originSections
        ),
      };
    }
    case submissionActionTypes.UPDATE_ORIGIN_SECTIONS_ORDER: {
      const { startIndex, endIndex } = action.payload;

      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      const originSectionsWithOrder = currentSubmissionCopy.origin.originSections;

      const [removed] = originSectionsWithOrder.splice(startIndex, 1);
      originSectionsWithOrder.splice(endIndex, 0, removed);

      currentSubmissionCopy.origin.originSections = originSectionsWithOrder.map(
        (sectionWithOrder, index) => ({
          ...sectionWithOrder,
          order: index + 1,
        })
      );

      return {
        ...state,
        currentSubmission: currentSubmissionCopy,
        isCurrentSubmissionTemplateEditorFormChanged: !isEqual(
          state.currentSubmissionDefaultOriginSections,
          currentSubmissionCopy.origin.originSections
        ),
      };
    }
    case submissionActionTypes.UPDATE_CURRENT_SUBMISSION_TEMPLATE_EDITOR_FORM_CHANGED_FLAG: {
      return {
        ...state,
        isCurrentSubmissionTemplateEditorFormChanged: action.payload.flag,
      };
    }
    case submissionActionTypes.SET_CURRENT_SUBMISSION_DEFAULT_ORIGIN_SECTIONS: {
      return {
        ...state,
        currentSubmissionDefaultOriginSections: action.payload,
      };
    }
    case submissionActionTypes.RESET_UNSAVED_CHANGES_TEMPLATE_EDITOR_FORM: {
      const currentSubmissionCopy = _cloneDeep(state.currentSubmission);
      currentSubmissionCopy.origin.originSections = state.currentSubmissionDefaultOriginSections;
      return {
        ...state,
        currentSubmission: currentSubmissionCopy,
        isCurrentSubmissionTemplateEditorFormChanged: false,
      };
    }
    default: {
      return state;
    }
  }
};
